import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import styles from './index.scss';
import stylesV2 from './notification-banner.module.css';
import * as actions from './data/actions';
import * as selectors from './data/selectors';
import {
  BannerMessage,
  NotificationBannerType,
  TranslatedBannerMessage,
} from './data/types';
import { useIsLastMountedInstanceOf } from 'signer-app/utils/use-is-last-mounted-instance-of';
import { Banner } from '@dropbox/dig-components/banner';
import { Box } from '@dropbox/dig-foundations';

type BannerProps = {
  message: BannerMessage | TranslatedBannerMessage;
  onClose: (id: BannerMessage['guid']) => void;
};

export function NotificationBanner({ message, onClose }: BannerProps) {
  const intl = useIntl();
  const { timeout, guid } = message;
  React.useEffect(() => {
    if (timeout) {
      const handle = setTimeout(() => onClose(guid), timeout);
      return () => clearTimeout(handle);
    }
    return () => {};
  }, [guid, onClose, timeout]);

  const bannerType = useMemo(() => {
    switch (message.type) {
      case NotificationBannerType.Err:
        return 'alert';
      case NotificationBannerType.Info:
        return 'attention';
      case NotificationBannerType.Success:
        return 'success';
      case NotificationBannerType.Warning:
        return 'warning';
      default:
        return 'alert';
    }
  }, [message.type]);

  const shouldShowOnTop = useMemo(() => {
    return message.guid === 'one-time-use-notification-banner-guid';
  }, [message.guid]);

  return (
    <Box
      marginX="auto"
      marginTop="Micro Small"
      className={classNames(stylesV2.banner, {
        [stylesV2.showOnTheTop]: shouldShowOnTop,
      })}
      data-qa-ref="notification-wrapper"
      data-testid="notification-wrapper"
    >
      <Banner
        type={bannerType}
        withCloseButton={`notification-${message.type}-close`}
        onRequestClose={() => onClose(message.guid)}
      >
        <Banner.Message>
          <span data-qa-ref="validation-error" data-testid="validation-error">
            {'messageDescriptor' in message
              ? intl.formatMessage(
                  message.messageDescriptor,
                  message.values || {},
                )
              : message.translatedMessage}
          </span>
        </Banner.Message>
      </Banner>
    </Box>
  );
}

export { NotificationBannerType };
type NotificationListProps = {
  className?: string;
  enableTimeOut?: boolean;
};
export default function NotificationList({
  className,
  enableTimeOut,
}: NotificationListProps) {
  const dispatch = useDispatch();
  const activeList = useIsLastMountedInstanceOf(NotificationList);

  const messageList = useSelector(selectors.getBannerMessages);

  const onClose = React.useCallback(
    (id: BannerMessage['guid']) => {
      dispatch(actions.removeBannerMessage(id));
    },
    [dispatch],
  );

  React.useEffect(() => {
    const timeOut = setTimeout(() => {
      if (enableTimeOut) {
        messageList.map((message) => {
          return onClose(message.guid);
        });
      }
    }, 4000);

    return () => {
      clearTimeout(timeOut);
    };
  }, [enableTimeOut, messageList, onClose]);

  if (!activeList) {
    return null;
  }
  const messageBanners = messageList.map((message) => {
    return (
      <NotificationBanner
        key={message.key || message.guid}
        message={message}
        onClose={onClose}
      />
    );
  });
  return (
    <div className={classNames(styles.container, className)}>
      {messageBanners}
    </div>
  );
}
